import { storeToRefs } from 'pinia'
import { fetchContent } from '@/utils/request'
import { useAuthStore } from '@/stores/auth'
import type IbexaContentTypeIdentifier from '@/enums/IbexaContentTypeIdentifier'
import type { Item } from '@/types/Ibexa'

export enum SortOrder {
  ascending = 'ascending',
  descending = 'descending'
}

export interface ISearchQuery {
  ['contentTypes[]']?: IbexaContentTypeIdentifier | IbexaContentTypeIdentifier[]
  fields?: string
  sortOrder?: SortOrder
  sortField?: string
  ['contentId[]']?: string | string[]
  ['subtrees[]']?: SubTree
  ['parentLocationId[]']?: string | string[]
  ['locationId[]']?: string | string[]
  limit?: string
  offset?: string
  mainLocation?: string
  type?: string
}

export enum SubTree {
  media = '/1/43/',
  VGS = '/1/2/265595/'
}

export const useCmsContentSearch = () => {
  const createQueryString = (searchQuery: ISearchQuery): string => {
    const auth = useAuthStore()
    const { isTeacher } = storeToRefs(auth)

    const defaultSearchQuery: ISearchQuery = {
      sortField: 'priority',
      limit: '999',
      ['subtrees[]']: SubTree.VGS,
      mainLocation: '0',
      type: isTeacher ? 'all' : '' // Value of "type" is irrelevant. Setting "type" to any value to differentiate teacher vs student caching
    }

    const arrayQueries = Object.entries(searchQuery).filter(([, value]) => Array.isArray(value))
    const nonArrayQueries = Object.entries(searchQuery)
      .filter(([key]) => !Array.isArray(searchQuery[key as keyof ISearchQuery]))
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})

    const searchParams = {
      ...defaultSearchQuery,
      ...nonArrayQueries,
    }

    const queries: string[] = []

    Object.entries(searchParams).forEach(([key, value]) => queries.push(`${key}=${value}`))
    arrayQueries.forEach(([key, value]) => value.forEach((item: string) => queries.push(`${key}=${item}`)))

    return queries.join('&')
  }

  async function search(searchQuery: ISearchQuery): Promise<Item[]> {
    const queryString = createQueryString(searchQuery)
    const response = await fetchContent(`/search?${queryString}`)

    return response.View.Result.searchHits.searchHit
  }

  return {
    search,
  }
}
